import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { EmptyState } from "@/components/common/EmptyState";
import { DealershipCard } from "@/components/common/DealershipCard";
import { DealershipFormDialog } from "@/components/dealerships/DealershipFormDialog";
import { SelectField } from "@/components/common/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Store } from "lucide-react";

export default function Dealerships() {
  const { isLeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [employee, setEmployee] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [dialog, setDialog] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (isLeader && employee !== "all") params.employee = employee;
    api.get("/dealerships", { params })
      .then((res) => setItems(res.data))
      .finally(() => setLoading(false));
  }, [search, employee, isLeader]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (isLeader) api.get("/users").then((res) => setEmployees(res.data.filter((u) => u.role === "employee")));
  }, [isLeader]);

  return (
    <div className="animate-fade-in-up space-y-4">
      <PageHeader
        title="Dealerships"
        subtitle={isLeader ? "All team dealerships" : "Your dealership master list"}
        actions={
          <Button variant="brand" onClick={() => setDialog(true)}>
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New Dealership</span>
          </Button>
        }
      />

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="h-11 pl-9" placeholder="Search name, city, contact, mobile…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {isLeader && (
          <SelectField
            className="sm:w-52" value={employee} onChange={setEmployee}
            options={[{ value: "all", label: "All employees" }, ...employees.map((e) => ({ value: e.id, label: e.full_name }))]}
            placeholder="Employee"
          />
        )}
      </div>

      {loading ? (
        <Loader />
      ) : items.length ? (
        <div className="grid gap-2.5 sm:grid-cols-2">
          {items.map((d) => <DealershipCard key={d.id} dealership={d} showOwner={isLeader} />)}
        </div>
      ) : (
        <EmptyState icon={Store} title="No dealerships found"
          description="Add a dealership to start capturing vehicle leads under it."
          action={<Button variant="brand" onClick={() => setDialog(true)}><Plus className="h-4 w-4" /> New Dealership</Button>} />
      )}

      <DealershipFormDialog open={dialog} onOpenChange={setDialog} onSaved={load} />
    </div>
  );
}
